import defaultTo from 'lodash/defaultTo';
import differenceBy from 'lodash/differenceBy';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import omitBy from 'lodash/omitBy';
import pickBy from 'lodash/pickBy';
import toNumber from 'lodash/toNumber';
import {
  isDiscretionaryDiscountPromotion,
  OfferingOption,
  SelectedPromotion,
  SelectPromotionSelectItem,
  UserTierOption,
} from '@css-xp/base';
import { getChargeElement } from '@css-xp/base/entitlement-utils';
import { PricingPlanType } from '@atlassian/cssxp-ccp-types';
import { abgGraphqlClient as graphqlClient } from '@atlassian/temp-client-utils';
import { BillEstimateOrderItemDto } from '@css-xp/common-services/src/api/gateways/ccp-gateways/src/ccp-api-order';
import { InvoiceGroup as InvoiceGroupV1, TransactionAccount } from '@css-xp/graphtypes';
import {
  BillingFrequency,
  BillingStatus,
  EntitlementDetails,
  FetchAvailableOfferingsForChangeSubscriptionQuery,
  FetchAvailablePartnerPromotionsForEntitlementQuery,
  FetchAvailablePricingPlansQuery,
  FetchAvailablePromotionsForEntitlementQuery,
  InvoiceGroup,
  LinkedDestinationEntitlement,
  Maybe,
  MetadataType,
  PricingPlan,
  PricingTiers,
  PricingType,
  Promotion,
  RelationshipPathType,
} from '@css-xp/graphtypes/src/generated-types-new';
import { getEntitlementDefaults } from '@atlassian/temp-proxies';
import { EntitlementDefaultsRequest, OrderItemType } from '@atlassian/cssxp-types-entitlement';
import {
  EndsAtType,
  LinkedEntitlementsInstance,
  OrderIntentRequest,
  PatchOperation,
  PatchRequestOperation,
  PricingPlanCycle,
  PricingPlanOption,
  PromotionInstance,
} from '@atlassian/cssxp-typescommon';
import {
  getDefaultPricingPlan,
  getPricingPlanDescription,
  getUserTier,
  getUserTierOptions,
  isFreePlanEntitlement,
  isLimitedFreePricingType,
  MARKETPLACE_PROMO_CODE,
  EntitlementEdition,
} from '@atlassian/cssxp-utils-entitlement';
import { isProductSkipTrialEligible, isStatuspage } from '@atlassian/cssxp-utils-product';
import { isTrialEntitlement } from '../../../../entitlement-page-utility/entitlement-summary/entitlment-summary-utils';
import { fetchAvailablePricingPlans } from '../../../../entitlement-page/src/queries/fetch-available-pricing-plans';
import { fetchAvailablePromotionsForEntitlementQuery } from '../../../../entitlement-page/src/queries/fetch-available-promotions-for-entitlement';
import { getCustomPromotionInstance } from '../../utils/promotion-utils';
import { DefaultAPIResponse, FormState } from './manage-entitlement-v2';
import { fetchAvailableOfferingsForChangeSubscription } from './queries/fetch-available-offerings-for-change-subscription';
import { fetchAvailablePartnerPromotionsForEntitlement } from '../../../../entitlement-page';

export type PromotionInstanceDetail = Promotion & { promotionInstanceId?: string };

const newAddedCustomPromotionValueResolver = (customPromotionId: string, formState: FormState, index: number) => {
  return {
    promotionDefinition: {
      promotionId: customPromotionId,
      customisedValues: !customPromotionId
        ? null
        : {
            applicationReason: {
              id: defaultTo(formState.customReason?.[index]?.value, ''),
            },
            benefits: [
              {
                duration: defaultTo(formState.customDuration?.[index]?.value, ''),
                iterations: defaultTo(toNumber(formState.customIterations?.[index]), undefined),
                value: defaultTo(toNumber(formState.customValue?.[index]), undefined),
              },
            ],
          },
    },
  };
};

const activeCustomPromotionValueResolver = (promotion: PromotionInstanceDetail) => {
  return {
    promotionDefinition: {
      promotionId: promotion.id,
      customisedValues: {
        applicationReason: {
          id: promotion.applicationReason?.id,
        },
        benefits: [
          {
            duration: defaultTo(promotion?.benefits?.[0]?.duration, ''),
            iterations: defaultTo(promotion?.benefits?.[0]?.iterations, undefined),
            value: defaultTo(promotion?.benefits?.[0]?.value, undefined),
          },
        ],
      },
    },
  };
};

export const getPromotionIds = (
  formState: FormState,
  isBillingCycleChanged?: boolean,
  entitlement?: EntitlementDetails | LinkedDestinationEntitlement
) => {
  const isStatusPageProduct = isStatuspage(entitlement?.productId);
  const promotionIds = [],
    customPromotions: PromotionInstance[] = [];
  const promotionKeys = Object.keys(formState).filter((key) => key.trim().includes('promotionId'));
  for (let i = 0; i < promotionKeys.length; i++) {
    if (isDiscretionaryDiscountPromotion(formState, promotionKeys[i])) {
      const customkey = `customPromotionId-${i}`;
      // @ts-ignore
      const customPromotionId = formState[customkey]?.value;
      customPromotions.push(newAddedCustomPromotionValueResolver(customPromotionId, formState, i));
    } else {
      const promotionObject: any = {
        promotionDefinition: {},
      };
      if (get(formState, promotionKeys[i])?.promotion?.type === MARKETPLACE_PROMO_CODE) {
        promotionObject.promotionDefinition.promotionCode = formState.promoCode;
      } else {
        promotionObject.promotionDefinition.promotionId = get(formState, `${promotionKeys[i]}.value`, '');
      }
      if (promotionObject.promotionDefinition.promotionId || promotionObject.promotionDefinition.promotionCode) {
        promotionIds.push(promotionObject);
      }
    }
  }

  if (isStatusPageProduct || !isBillingCycleChanged) {
    defaultTo(
      differenceBy(
        entitlement?.promotions as PromotionInstanceDetail[],
        defaultTo(
          formState?.promotionsToRemove?.map((item: any) => item?.promotion),
          []
        ),
        'id'
      ),
      []
    ).forEach((promotion: PromotionInstanceDetail) => {
      if (!isNil(promotion)) {
        promotionIds.push({ promotionInstanceId: promotion?.promotionInstanceId });
      }
    });

    const isOfferingChanged = formState?.offering?.value !== entitlement?.offering?.id;
    if (!isOfferingChanged) {
      differenceBy(
        entitlement?.scheduledChanges?.promotions as PromotionInstanceDetail[],
        defaultTo(entitlement?.promotions as PromotionInstanceDetail[], []),
        'promotionInstanceId'
      ).forEach((promotion: PromotionInstanceDetail) => {
        if (!isNil(promotion)) {
          const removedScheduledPromotionId = formState?.schedulePromotionsToRemove?.find(
            (scheduledPromotion: any) => scheduledPromotion?.promotion?.id === promotion?.id
          );
          if (!removedScheduledPromotionId) {
            if (promotion.isPromotionCustomisable) {
              promotionIds.push(activeCustomPromotionValueResolver(promotion));
            } else {
              const promotionDefinition: PromotionInstance['promotionDefinition'] = {};

              if (promotion.promotionCode) {
                promotionDefinition.promotionCode = promotion.promotionCode;
              } else {
                promotionDefinition.promotionId = promotion.id as string;
              }

              promotionIds.push({ promotionDefinition });
            }
          }
        }
      });
    }
  }
  if (formState?.partnerPromotion?.id) {
    promotionIds.push({
      promotionDefinition: {
        promotionId: formState?.partnerPromotion?.id,
      },
    });
  }
  return { promotionIds, customPromotions };
};

export const getAppliedPromotions = (
  formState: FormState,
  isBillingCycleChanged?: boolean,
  entitlement?: EntitlementDetails | LinkedDestinationEntitlement
) => {
  const isStatusPageProduct = isStatuspage(entitlement?.productId);
  const selectedPromo: SelectedPromotion[] = [];
  for (let i = 0; ; i++) {
    const promotionItem = get(formState, `promotionId-${i}`, '');
    if (promotionItem) {
      if (isDiscretionaryDiscountPromotion(promotionItem)) {
        const customPromotion = get(formState, `customPromotionId-${i}`, '');
        selectedPromo.push({
          ...customPromotion?.promotion,
          customFormField: {
            iterations: defaultTo(get(formState, 'customIterations'), [])[i],
            value: defaultTo(get(formState, 'customValue'), [])[i],
            reason: defaultTo(get(formState, 'customReason'), [])[i],
            duration: defaultTo(get(formState, 'customDuration'), [])[i],
          },
        });
      } else {
        selectedPromo.push(promotionItem.promotion);
      }
    } else break;
  }

  if (isStatusPageProduct || !isBillingCycleChanged) {
    defaultTo(
      differenceBy(
        entitlement?.promotions as PromotionInstanceDetail[],
        defaultTo(
          formState?.promotionsToRemove?.map((item: any) => item?.promotion),
          []
        ),
        'id'
      ),
      []
    ).forEach((promotion: PromotionInstanceDetail) => {
      if (!isNil(promotion)) {
        selectedPromo.push(promotion);
      }
    });

    const isOfferingChanged = formState?.offering?.value !== entitlement?.offering?.id;
    if (!isOfferingChanged) {
      differenceBy(
        entitlement?.scheduledChanges?.promotions as PromotionInstanceDetail[],
        defaultTo(entitlement?.promotions as PromotionInstanceDetail[], []),
        'promotionInstanceId'
      ).forEach((promotion: PromotionInstanceDetail) => {
        if (!isNil(promotion)) {
          const removedScheduledPromotionId = formState?.schedulePromotionsToRemove?.find(
            (scheduledPromotion: any) => scheduledPromotion?.promotion?.id === promotion?.id
          );
          if (!removedScheduledPromotionId) {
            selectedPromo.push(promotion);
          }
        }
      });
    }
  }
  if (formState?.partnerPromotion?.id) {
    selectedPromo.push(formState?.partnerPromotion);
  }
  return selectedPromo.filter((each) => !isEqual(each, { type: '', id: '' }));
};

export const getDiscountAmount = (items: BillEstimateOrderItemDto['items'] | undefined) => {
  if (items && items.length > 0) {
    const nonDstItems = items.filter((item) => item?.metadata?.type !== MetadataType.ADMIN_FEE);
    const flattenAdjustments = (
      flattenedList: BillEstimateOrderItemDto['items'][0]['adjustments'],
      item: BillEstimateOrderItemDto['items'][0]
    ) => {
      const allAdjustmentsForItem = item?.adjustments ?? [];
      flattenedList = flattenedList.concat(allAdjustmentsForItem);
      return flattenedList;
    };
    const allAdjustments = nonDstItems.reduce(flattenAdjustments, []);

    const discount = allAdjustments.reduce((sum, currentAdjustment) => {
      sum += currentAdjustment.amount;
      return sum;
    }, 0);
    return discount;
  }
  return null;
};

export const fetchAvailableOfferings = async (entitlementId: string) => {
  return await graphqlClient().query<FetchAvailableOfferingsForChangeSubscriptionQuery>({
    query: fetchAvailableOfferingsForChangeSubscription,
    variables: { id: entitlementId },
    fetchPolicy: 'no-cache',
  });
};

export const fetchPricingPlans = async ({
  entitlementNumber,
  currency,
  billingCycle,
  offeringId,
  planType,
}: {
  entitlementNumber: Maybe<string> | undefined;
  currency: Maybe<string>;
  billingCycle: BillingFrequency | undefined;
  offeringId: Maybe<string> | undefined;
  planType: Maybe<string> | undefined;
}) => {
  const filterOptions = [
    {
      key: 'currency',
      value: currency,
    },
    {
      key: 'offeringId',
      value: offeringId,
    },
    {
      key: 'billingCycle',
      value: billingCycle ?? BillingFrequency.MONTHLY,
    },
  ];
  if (planType) {
    filterOptions.push({
      key: 'planType',
      value: planType,
    });
  }

  return await graphqlClient().query<FetchAvailablePricingPlansQuery>({
    query: fetchAvailablePricingPlans,
    variables: {
      id: entitlementNumber,
      filterOptions,
    },
    fetchPolicy: 'cache-first',
  });
};

export const getAvailablePromotionsQuery = async (
  entitlementNumber?: string | null,
  intendedBillingPeriodState?: string
) => {
  return await graphqlClient().query<FetchAvailablePromotionsForEntitlementQuery>({
    query: fetchAvailablePromotionsForEntitlementQuery,
    variables: {
      id: entitlementNumber,
      intendedBillingPeriodState,
    },
    fetchPolicy: 'cache-first',
  });
};

export const getPartnerPromotionQuery = async ({
  entitlementNumber,
  intendedBillingPeriodState,
  billingFrequency,
  discountTier,
  programLevelGlobal,
}: {
  entitlementNumber?: string | null;
  intendedBillingPeriodState?: string;
  billingFrequency?: BillingFrequency;
  discountTier?: Maybe<string> | undefined;
  programLevelGlobal?: Maybe<string> | undefined;
}) => {
  return await graphqlClient().query<FetchAvailablePartnerPromotionsForEntitlementQuery>({
    query: fetchAvailablePartnerPromotionsForEntitlement,
    variables: {
      id: entitlementNumber,
      intendedBillingPeriodState,
      billingFrequency,
      discountTier,
      programLevelGlobal,
    },
    fetchPolicy: 'cache-first',
  });
};

export const getDefaultAPIEntitlementData = async ({
  entitlement,
  entitlementId,
  offeringId,
  billingCycle,
}: {
  entitlement: EntitlementDetails | LinkedDestinationEntitlement;
  entitlementId: string;
  offeringId: Maybe<string> | undefined;
  billingCycle: BillingFrequency;
}) => {
  const requestParams: EntitlementDefaultsRequest = {
    transactionAccountId: defaultTo(entitlement?.transactionAccountId, ''),
    offeringId: offeringId as string,
    billingCycle,
  };
  if (entitlement?.baseProduct?.toLowerCase().includes('statuspage') && entitlement?.parentSen) {
    // is Statuspage add on, then pass the parent statuspage
    requestParams.currentEntitlementId = entitlement?.parentSen;
  } else if (entitlement?.isApp) {
    const appDependenceEntitlement = defaultTo(entitlement?.relatesFromEntitlements, []).filter(
      (relations) => relations?.relationshipType === RelationshipPathType.APP_DEPENDENCE
    );
    // is an JCA app, then pass the app parent JCA subscription
    requestParams.relatedEntitlementId = defaultTo(appDependenceEntitlement?.[0]?.entitlementId, undefined);
    requestParams.relationshipType = RelationshipPathType.APP_DEPENDENCE;
  } else {
    requestParams.currentEntitlementId = entitlementId;
  }

  let result;
  try {
    result = await getEntitlementDefaults(requestParams);
  } catch (e) {
    //silent default api error
  }

  return {
    ...result,
    offeringId: offeringId as string,
  };
};

export const getFormValuesForCurrentEntitlement = ({
  entitlement,
  linkedEntitlements,
  isAnnualTermEnabled,
  isBillingCycleChanged,
}: {
  entitlement: EntitlementDetails;
  linkedEntitlements: LinkedDestinationEntitlement[];
  isAnnualTermEnabled: boolean;
  isBillingCycleChanged: boolean;
}) => {
  let userTier: UserTierOption | undefined | null;
  let units: string | undefined;
  const isStatusPageProduct = isStatuspage(entitlement.productId);

  if (isStatusPageProduct) {
    // Return unit count as '1' for FREE SP entitlements , since UTS data cant be relied on in this particular case .
    units = isFreePlanEntitlement(entitlement) ? '1' : defaultTo(entitlement?.unitCount, '1');
  } else if (entitlement.billingFrequency === BillingFrequency.ANNUAL) {
    if (entitlement.isApp) {
      userTier = getAppUserTier({
        linkedEntitlements,
        userTier: getUserTier(entitlement?.subscription),
        pricingPlanTiers: entitlement.pricingPlan?.tiers,
      });
    } else {
      const userTierValue = getUserTier(entitlement?.subscription);
      const userTierObj = entitlement.pricingPlan?.tiers?.find((item) => item?.ceiling === userTierValue);
      const userTierLabel = `${userTierObj?.floor}-${userTierObj?.ceiling}`;
      userTier = userTierValue
        ? {
            label: userTierLabel,
            value: userTierValue,
          }
        : null;
    }
  }

  return {
    offering: entitlement?.offering?.id
      ? {
          label: entitlement.offering.name,
          value: entitlement.offering.id,
          pricingType: entitlement.offering.pricingType,
          level: entitlement.offering.level,
          status: entitlement.offering.status,
        }
      : null,
    pricingPlan: entitlement?.pricingPlan?.id
      ? {
          label: getPricingPlanDescription(entitlement?.pricingPlan),
          value: entitlement.pricingPlan.id,
          tiers: entitlement.pricingPlan.tiers,
          chargeElement: entitlement.pricingPlan.chargeElement,
          chargeType: entitlement.pricingPlan.chargeType,
          type: entitlement.pricingPlan.type,
          pricingPlanType: entitlement.pricingPlan.pricingPlanType,
        }
      : null,
    userTier,
    units,
    skipTrialEligible: isProductSkipTrialEligible(defaultTo(entitlement.offering?.productKey, ''), !!entitlement.isApp),
    ...getDefaultSkipTrialValues(entitlement, isAnnualTermEnabled, isBillingCycleChanged),
  };
};

export const getIntendedBillingPeriodState = (isSkipTrialChecked?: boolean) => {
  return isSkipTrialChecked ? 'PAID' : 'TRIAL';
};

export const pickPromotionFields = (formState: FormState) => {
  return pickBy(
    formState,
    (_value, key) => key.startsWith('promoCode') || key.startsWith('promotionId-') || key.startsWith('custom')
  );
};

export const getFormStateWithoutRemovedPromotionFields = (formState: FormState, formData: any) => {
  const formDataPromotionFields = pickPromotionFields(formData);
  const formStatePromotionFields = pickPromotionFields(formState);
  const omittedFormState = omitBy(
    formState,
    (_value, key) => !(key in formDataPromotionFields) && key in formStatePromotionFields
  ) as FormState;
  return omittedFormState;
};

export const getFormStateWithoutAddedPromotionFields = (formState: FormState) => {
  return omitBy(
    formState,
    (_value, key) => key.startsWith('promoCode') || key.startsWith('promotionId-') || key.startsWith('custom')
  ) as FormState;
};

export const evaluateOffering = ({
  selectedOffering,
  entitlement,
  isBillingCycleChanged,
  isAnnualTermEnabled,
}: {
  selectedOffering: OfferingOption;
  entitlement: EntitlementDetails | LinkedDestinationEntitlement;
  isBillingCycleChanged?: boolean;
  isAnnualTermEnabled: boolean;
}) => {
  const currentOffering = entitlement.offering;
  const isStatusPageProduct = isStatuspage(entitlement.productId);
  const isTrial = isTrialEntitlement(entitlement, isStatusPageProduct);
  const result: {
    skipTrialEligible?: boolean;
    skipTrial?: boolean;
    skipTrialDisabled?: boolean;
  } = {};

  if (isStatusPageProduct) {
    result.skipTrialEligible = false;

    return result;
  }

  if (!isAnnualTermEnabled && isBillingCycleChanged && !isFreePlanEntitlement(entitlement)) {
    result.skipTrialEligible = true;
    result.skipTrial = true;
    result.skipTrialDisabled = true;
    return result;
  }

  if (selectedOffering?.pricingType === PricingType.FREE) {
    result.skipTrialEligible = true;
    result.skipTrial = true;
    result.skipTrialDisabled = true;
    return result;
  } else if (isLimitedFreePricingType(selectedOffering?.pricingType)) {
    result.skipTrialEligible = true;
    result.skipTrial = false;
    result.skipTrialDisabled = true;
    return result;
  }

  if (!currentOffering?.level || !selectedOffering?.level) return null;

  if (currentOffering?.level === selectedOffering?.level) {
    if (!isTrial) {
      result.skipTrialEligible = true;
      result.skipTrial = false;
      result.skipTrialDisabled = true;
    } else {
      result.skipTrialEligible = true;
      result.skipTrial = false;
      result.skipTrialDisabled = false;
    }
  } else if (currentOffering?.level < selectedOffering?.level) {
    result.skipTrialEligible = true;
    // plan Upgrade - not accounting any user/qty change
    result.skipTrial = false;
    result.skipTrialDisabled = false;
  } else {
    result.skipTrialEligible = true;
    // plan downgrade - not accounting any user/qty change
    if (isTrial) {
      result.skipTrial = false;
      result.skipTrialDisabled = false;
    } else {
      result.skipTrial = true;
      result.skipTrialDisabled = true;
    }
  }

  return result;
};

// To determine if changed offering is FREE and have pricing type FREE .
export const isImmediateAmendmentToFreeOrder = (
  entitlement: EntitlementDetails | LinkedDestinationEntitlement,
  selectedOffering: OfferingOption
): boolean => {
  return entitlement.offeringId !== selectedOffering?.value && selectedOffering?.pricingType === PricingType.FREE;
};

export const getPromotionsRequestBody = ({
  entitlement,
  formStateValues,
  patchRemovePromotionEnabled,
}: {
  entitlement: EntitlementDetails | LinkedDestinationEntitlement;
  formStateValues: FormState;
  patchRemovePromotionEnabled: boolean;
}) => {
  const promotionRequest: any = {};
  let scheduleRemovePromotions: any = [];
  const activePromotions = entitlement.promotions || [];
  const currentSchedulePromotions = entitlement.scheduledChanges?.promotions?.map((promotion, index) => {
    return {
      promotion: {
        ...promotion,
        index: index.toString(),
      },
    };
  }) as Promotion[];
  const scheduledPromotionOperations: PatchRequestOperation[] = [];
  promotionRequest.promotionsToRemove = [];

  if (patchRemovePromotionEnabled) {
    if (
      formStateValues.promotionsToRemove &&
      formStateValues.promotionsToRemove?.length > 0 &&
      currentSchedulePromotions &&
      currentSchedulePromotions?.length > 0
    ) {
      //@ts-ignore
      formStateValues.schedulePromotionsToRemove = currentSchedulePromotions;
      //@ts-ignore
      (formStateValues.promotionsToRemove || []).forEach((promo: any, index: number) => {
        scheduledPromotionOperations.push({
          op: PatchOperation.REMOVE,
          path: `/subscription/promotionInstances/${index}`,
        });
      });
    }

    let ind = -1;
    (formStateValues.schedulePromotionsToRemove || []).forEach((promo: any) => {
      if (
        activePromotions.some((activepromotion: any, index: number) => {
          ind = index;
          return activepromotion?.id === promo.promotion.id;
        })
      ) {
        scheduledPromotionOperations.push({
          op: PatchOperation.REMOVE,
          path: `/subscription/promotionInstances/${ind}`,
        });
      }
      scheduledPromotionOperations.push({
        op: PatchOperation.REMOVE,
        path: `/subscription/scheduledChanges/promotionInstances/${promo.promotion.index}`,
      });
      promotionRequest.promotionsToRemove?.push(promo.promotion.promotionInstanceId);
    });

    promotionRequest.operations = [...scheduledPromotionOperations];
  } else {
    scheduleRemovePromotions = (formStateValues.schedulePromotionsToRemove || []).map((promotion: any) => {
      promotionRequest.promotionsToRemove?.push(promotion.promotion.promotionInstanceId);
      return {
        promotionDefinition: {
          promotionId: promotion.promotion.promotionInstanceId,
        },
      };
    });
  }

  const promotions = Object.keys(formStateValues).filter((key) => key.trim().includes('promotionId'));
  const promotionsToAdd: any[] = [];

  promotions?.forEach((key: string, index: number) => {
    let promotionToAdd;

    if (!isDiscretionaryDiscountPromotion(formStateValues, key)) {
      promotionToAdd = {
        promotionInstanceId: null,
        promotionDefinition: {
          promotionId: null,
          promotionCode: null,
          customisedValues: null,
        },
      };

      if (get(formStateValues, key)?.promotion?.type === MARKETPLACE_PROMO_CODE) {
        //@ts-ignore
        promotionToAdd.promotionDefinition.promotionCode = formStateValues.promoCode;
      } else {
        promotionToAdd.promotionDefinition.promotionId = get(formStateValues, `${key}.value`, '');
      }
    } else {
      promotionToAdd = getCustomPromotionInstance(index, formStateValues);
    }

    promotionToAdd && promotionsToAdd.push(promotionToAdd);
  });

  if (formStateValues?.partnerPromotion?.id) {
    promotionsToAdd.push({
      promotionInstanceId: null,
      promotionDefinition: {
        promotionId: formStateValues?.partnerPromotion?.id,
        promotionCode: null,
        customisedValues: null,
      },
    });
  }

  const filterValidPromotion = (selectedPromo: SelectPromotionSelectItem) => {
    const promotion = selectedPromo?.promotion;
    return !!promotion?.id;
  };

  const filteredPromotionsToAdd = promotionsToAdd.filter(
    (promotionToAdd) =>
      promotionToAdd.promotionDefinition.promotionId ||
      promotionToAdd.promotionDefinition.promotionCode ||
      promotionToAdd.promotionDefinition.customisedValues
  ); //@ts-ignore
  let removePromotions: PromotionInstance[] = defaultTo(formStateValues.promotionsToRemove, [])
    .filter(filterValidPromotion)
    .map((promotionItem) => ({
      promotionDefinition: {
        promotionId: defaultTo(promotionItem?.promotion?.id, undefined),
        //@ts-ignore
        promotionCode: defaultTo(promotionItem?.promotion?.promotionCode, undefined),
        customisedValues: promotionItem?.promotion?.isPromotionCustomisable
          ? {
              applicationReason: {
                id: defaultTo(promotionItem?.promotion?.applicationReason?.id, ''),
              },
              benefits: [
                {
                  value: defaultTo(promotionItem?.promotion?.benefits?.[0]?.value, undefined),
                  duration: defaultTo(promotionItem?.promotion?.benefits?.[0]?.duration, ''),
                  iterations: defaultTo(promotionItem?.promotion?.benefits?.[0]?.iterations, null),
                },
              ],
            }
          : null,
      },
    }));
  if (scheduleRemovePromotions.length > 0) {
    removePromotions = removePromotions.concat(scheduleRemovePromotions);
  }

  return {
    ...promotionRequest,
    removePromotions,
    addPromotions: filteredPromotionsToAdd,
  };
};

// this function should be moved to common/utils-entitlement but for that isTrialEntitlement also needs to move there from entitlements-v2
export const getDefaultSkipTrialValues = (
  entitlement: EntitlementDetails | LinkedDestinationEntitlement,
  isAnnualTermEnabled: boolean,
  isBillingCycleChanged: boolean
) => {
  /*
   if limited free offering  -> skipTrial: false , skipTrialDisabled: true
   if annual term is disabled and billingCycle is changed -> skipTrial: true,  skipTrialDisabled:true, else rely on isOnTrial
   if annual term is enabled and it is not limited free offering, in that case skip trial values depend on isOnTrial
    */
  const isStatusPageProduct = isStatuspage(entitlement.productId);
  const isOnTrial = isTrialEntitlement(entitlement, isStatusPageProduct);
  const isLimitedFreeOffering = isLimitedFreePricingType(entitlement.offering?.pricingType);

  if (isLimitedFreeOffering) {
    return {
      skipTrial: false,
      skipTrialDisabled: true,
    };
  }

  if (entitlement.upgradeTrial) {
    return {
      skipTrial: true,
      skipTrialDisabled: true,
    };
  }

  if (!isAnnualTermEnabled && isBillingCycleChanged) {
    return {
      skipTrial: true,
      skipTrialDisabled: true,
    };
  }

  return {
    skipTrial: !isOnTrial,
    skipTrialDisabled: !isOnTrial,
  };
};

export const getPricingEligibility = ({
  invoiceGroup,
  transactionAccount,
}: {
  invoiceGroup?: InvoiceGroup | InvoiceGroupV1 | null;
  transactionAccount?: TransactionAccount | null;
}): PricingPlanType | null => {
  if (invoiceGroup) {
    if (invoiceGroup?.shipToPriceEligibility) {
      return invoiceGroup?.shipToPriceEligibility?.toUpperCase() as PricingPlanType;
    } else if (transactionAccount?.billToPartyPricingEligibilty) {
      return transactionAccount?.billToPartyPricingEligibilty?.toUpperCase() as PricingPlanType;
    } else {
      return PricingPlanType.COMMERCIAL;
    }
  }
  return null;
};

export const getAppUserTier = ({
  entitlement,
  linkedEntitlements,
  userTier,
  pricingPlanTiers,
}: {
  entitlement?: EntitlementDetails;
  linkedEntitlements: LinkedDestinationEntitlement[];
  pricingPlanTiers?: Maybe<Maybe<PricingTiers>[]>;
  userTier?: Maybe<number>;
}) => {
  let maxUserTier: UserTierOption | null | undefined;
  let maxUserTiervalue: Maybe<number> | undefined = 0;

  if (entitlement) {
    maxUserTiervalue = getUserTier(entitlement?.subscription) ?? 0;
    const userTierOptions = getUserTierOptions({
      chargeQuantity: maxUserTiervalue,
      userTiers: entitlement.pricingPlan?.tiers,
    }) as UserTierOption[];

    maxUserTier = userTierOptions?.[0] ?? null;
  }

  linkedEntitlements
    ?.filter(
      (item) => !item.isApp && item.billingStatus === BillingStatus.ACTIVE && item.billingFrequency && item.userTier
    )
    ?.forEach((linkedEntitlement) => {
      //@ts-ignore
      if (linkedEntitlement?.userTier > maxUserTiervalue) {
        maxUserTiervalue = linkedEntitlement.userTier;
        const userTierOptions = getUserTierOptions({
          chargeQuantity: maxUserTiervalue,
          userTiers: linkedEntitlement.pricingPlan?.tiers,
        }) as UserTierOption[];

        maxUserTier = userTierOptions?.[0] ?? null;
      }
    });

  if (!maxUserTiervalue) {
    const userTierOptions = getUserTierOptions({
      chargeQuantity: userTier,
      userTiers: pricingPlanTiers,
    }) as UserTierOption[];

    maxUserTier = userTierOptions?.[0] ?? null;
  }

  return maxUserTier;
};

export const getAppUserTierForBillingCycleChange = ({
  entitlement,
  linkedEntitlements,
  defaultApiData,
  availablePricingPlansData,
  pricingPlanTiers,
  activeUsers,
}: {
  entitlement?: EntitlementDetails;
  linkedEntitlements: LinkedDestinationEntitlement[];
  defaultApiData: {
    [key: string]: DefaultAPIResponse;
  };
  availablePricingPlansData: {
    [key: string]: Maybe<Array<Maybe<PricingPlan>>> | undefined;
  };
  pricingPlanTiers?: Maybe<Maybe<PricingTiers>[]>;
  activeUsers: Maybe<number> | undefined;
}) => {
  let maxUserTier: UserTierOption | null | undefined;
  let maxUserTiervalue = 0;

  if (entitlement) {
    maxUserTiervalue = defaultApiData?.[entitlement.offeringId as string]?.chargeQuantities?.[0]?.quantity ?? 0;
    const pricingPlans = availablePricingPlansData?.[entitlement.id];
    const defaultPricingPlanId = defaultApiData?.[entitlement.offeringId as string]?.pricingPlanId?.value;
    const defaultPricingPlan = !defaultPricingPlanId
      ? getDefaultPricingPlan(pricingPlans)
      : pricingPlans?.find((item) => item?.id === defaultPricingPlanId);
    const userTierOptions = getUserTierOptions({
      chargeQuantity: maxUserTiervalue,
      userTiers: defaultPricingPlan?.tiers,
    }) as UserTierOption[];

    maxUserTier = userTierOptions?.[0] ?? null;
  }

  linkedEntitlements
    ?.filter((item) => !item.isApp && item.billingStatus === BillingStatus.ACTIVE)
    ?.forEach((linkedEntitlement) => {
      const chargeQuantity =
        defaultApiData?.[linkedEntitlement.offeringId as string]?.chargeQuantities?.[0]?.quantity ?? 0;

      if (chargeQuantity > maxUserTiervalue) {
        maxUserTiervalue = chargeQuantity;
        const pricingPlans = availablePricingPlansData?.[linkedEntitlement.entitlementId];
        const defaultPricingPlanId = defaultApiData?.[linkedEntitlement.offeringId as string]?.pricingPlanId?.value;
        const defaultPricingPlan = !defaultPricingPlanId
          ? getDefaultPricingPlan(pricingPlans)
          : pricingPlans?.find((item) => item?.id === defaultPricingPlanId);
        const userTierOptions = getUserTierOptions({
          chargeQuantity: maxUserTiervalue,
          userTiers: defaultPricingPlan?.tiers,
        }) as UserTierOption[];

        maxUserTier = userTierOptions?.[0] ?? null;
      }
    });

  if (!maxUserTiervalue) {
    const userTierOptions = getUserTierOptions({
      activeUsers,
      userTiers: pricingPlanTiers,
    }) as UserTierOption[];
    maxUserTier = userTierOptions?.[0] ?? null;
  }

  return maxUserTier;
};

export const getFormValuesForLinkedEntitlements = ({
  entitlement,
  formValues,
  linkedEntitlements,
  filteredLinkedEntitlements,
  availablePricingPlansData,
  defaultApiData,
  isAnnualTermEnabled,
  isBillingCycleChanged,
}: {
  entitlement: EntitlementDetails;
  formValues: FormState;
  linkedEntitlements: LinkedDestinationEntitlement[];
  filteredLinkedEntitlements: LinkedDestinationEntitlement[];
  availablePricingPlansData: {
    [key: string]: Maybe<Array<Maybe<PricingPlan>>> | undefined;
  };
  defaultApiData: {
    [key: string]: DefaultAPIResponse;
  };
  isAnnualTermEnabled: boolean;
  isBillingCycleChanged: boolean;
}) => {
  filteredLinkedEntitlements?.forEach((linkedEntitlement: LinkedDestinationEntitlement) => {
    let userTierOption: UserTierOption | null | undefined, pricingPlanOption: PricingPlanOption | null | undefined;
    let defaultSkiptTrialValues;

    if (
      formValues.billingFrequency === BillingFrequency.ANNUAL &&
      linkedEntitlement.billingFrequency === BillingFrequency.ANNUAL
    ) {
      defaultSkiptTrialValues = getDefaultSkipTrialValues(
        linkedEntitlement,
        isAnnualTermEnabled,
        isBillingCycleChanged
      );
      if (linkedEntitlement.isApp) {
        userTierOption = getAppUserTier({
          linkedEntitlements,
          entitlement,
          userTier: linkedEntitlement.userTier,
          pricingPlanTiers: linkedEntitlement.pricingPlan?.tiers,
        });
      } else {
        const userTierValue = linkedEntitlement.userTier;
        const userTierObj = linkedEntitlement.pricingPlan?.tiers?.find((item) => item?.ceiling === userTierValue);
        const userTierLabel = `${userTierObj?.floor}-${userTierObj?.ceiling}`;
        userTierOption = userTierValue
          ? {
              label: userTierLabel,
              value: userTierValue,
            }
          : null;
      }

      pricingPlanOption = linkedEntitlement.pricingPlan?.id
        ? {
            label: getPricingPlanDescription(linkedEntitlement.pricingPlan),
            value: linkedEntitlement.pricingPlan.id,
            tiers: linkedEntitlement.pricingPlan.tiers,
            chargeElement: linkedEntitlement.pricingPlan.chargeElement,
            chargeType: linkedEntitlement.pricingPlan.chargeType,
            type: linkedEntitlement.pricingPlan.type,
            pricingPlanType: linkedEntitlement.pricingPlan.pricingPlanType,
          }
        : null;
    } else {
      defaultSkiptTrialValues = getDefaultSkipTrialValues(
        linkedEntitlement,
        isAnnualTermEnabled,
        isBillingCycleChanged
      );
      const pricingPlans = availablePricingPlansData?.[linkedEntitlement.entitlementId];
      const defaultPricingPlanId = defaultApiData?.[linkedEntitlement.offeringId as string]?.pricingPlanId?.value;
      const defaultPricingPlan = !defaultPricingPlanId
        ? getDefaultPricingPlan(pricingPlans)
        : pricingPlans?.find((item) => item?.id === defaultPricingPlanId);

      pricingPlanOption = defaultPricingPlan?.id
        ? {
            label: getPricingPlanDescription(defaultPricingPlan),
            value: defaultPricingPlan.id,
            tiers: defaultPricingPlan.tiers,
            chargeElement: defaultPricingPlan.chargeElement,
            chargeType: defaultPricingPlan.chargeType,
            type: defaultPricingPlan.type,
            pricingPlanType: defaultPricingPlan.pricingPlanType,
          }
        : null;

      if (formValues.billingFrequency === BillingFrequency.ANNUAL) {
        if (linkedEntitlement.isApp) {
          userTierOption = getAppUserTierForBillingCycleChange({
            entitlement,
            linkedEntitlements,
            pricingPlanTiers: defaultPricingPlan?.tiers,
            availablePricingPlansData,
            defaultApiData,
            activeUsers: linkedEntitlement.activeUsers,
          });
        } else {
          const chargeQuantity =
            defaultApiData?.[linkedEntitlement.offeringId as string]?.chargeQuantities?.[0]?.quantity;
          const userTierOptions = getUserTierOptions({
            chargeQuantity,
            activeUsers: linkedEntitlement.activeUsers,
            userTiers: defaultPricingPlan?.tiers,
          }) as UserTierOption[];
          userTierOption = userTierOptions?.[0] ?? null;
        }
      }
    }

    formValues[linkedEntitlement.entitlementId] = {
      offering: linkedEntitlement.offering?.id
        ? {
            label: linkedEntitlement.offering.name,
            value: linkedEntitlement.offering.id,
            pricingType: linkedEntitlement.offering.pricingType,
            level: linkedEntitlement.offering.level,
            status: linkedEntitlement.offering.status,
          }
        : null,
      pricingPlan: pricingPlanOption,
      userTier: userTierOption,
      ...defaultSkiptTrialValues,
      skipTrialEligible: isProductSkipTrialEligible(
        defaultTo(linkedEntitlement.offering?.productKey, ''),
        !!linkedEntitlement.isApp
      ),
    };
  });

  return formValues;
};

export const getHiddenFieldConfig = ({
  entitlement,
  isBillingCycleChanged,
  formState,
}: {
  entitlement: EntitlementDetails | LinkedDestinationEntitlement;
  isBillingCycleChanged: boolean;
  formState: FormState;
}) => {
  const isStatusPageProduct = isStatuspage(entitlement.productId);
  return {
    schedulePromotions:
      (!isStatusPageProduct && isBillingCycleChanged) || formState.offering?.value !== entitlement.offeringId,
    activePromotions: !isStatusPageProduct && isBillingCycleChanged,
    offering: !!entitlement.parentSen,
    skipTrial: !formState.skipTrialEligible,
  };
};

export const checkisFormDirty = ({
  entitlement,
  updatedFormState,
  pristineFormState,
  isBillingCycleChanged,
}: {
  entitlement?: EntitlementDetails | LinkedDestinationEntitlement;
  updatedFormState: FormState;
  pristineFormState: FormState | undefined;
  isBillingCycleChanged: boolean;
}) => {
  if (isBillingCycleChanged || !!entitlement?.upgradeTrial) {
    return true;
  }

  const currentEntUpdatedFormState = {
    offeringId: updatedFormState.offering?.value,
    pricingPlanId: updatedFormState.pricingPlan?.value,
    userTier: updatedFormState.userTier?.value,
    units: updatedFormState.units,
    skipTrial: updatedFormState.skipTrial,
    skipTrialEligible: updatedFormState.skipTrialEligible,
    skipTrialDisabled: updatedFormState.skipTrialDisabled,
  };
  const currentEntPristineFormState = {
    offeringId: pristineFormState?.offering?.value,
    pricingPlanId: pristineFormState?.pricingPlan?.value,
    userTier: pristineFormState?.userTier?.value,
    units: pristineFormState?.units,
    skipTrial: pristineFormState?.skipTrial,
    skipTrialEligible: pristineFormState?.skipTrialEligible,
    skipTrialDisabled: pristineFormState?.skipTrialDisabled,
  };

  return !isEqual(currentEntUpdatedFormState, currentEntPristineFormState);
};

export const getFinalLinkedEntitlements = ({
  updatedFormState,
  pristineFormState,
  filteredLinkedEntitlements,
  isBillingCycleChanged,
}: {
  updatedFormState: FormState;
  pristineFormState: FormState | undefined;
  filteredLinkedEntitlements: LinkedDestinationEntitlement[];
  isBillingCycleChanged: boolean;
}) => {
  if (isBillingCycleChanged) {
    return [...filteredLinkedEntitlements];
  }

  const result: LinkedDestinationEntitlement[] = [];

  Object.keys(updatedFormState)?.forEach((key) => {
    const linkedEnt = filteredLinkedEntitlements.find((item) => item.entitlementId === key);

    if (linkedEnt) {
      const isDirty = checkisFormDirty({
        updatedFormState: updatedFormState[linkedEnt.entitlementId],
        pristineFormState: pristineFormState?.[linkedEnt.entitlementId],
        isBillingCycleChanged,
      });

      isDirty && result.push(linkedEnt);
    }
  });

  return result;
};

export const isTierChangeAllowed = ({
  activeUsers,
  userTier,
}: {
  activeUsers: Maybe<number> | undefined | null;
  userTier: Maybe<number> | undefined | null;
}) => {
  const usageCount = activeUsers ?? 0;

  if (!isNil(userTier)) {
    if (usageCount <= userTier) {
      return true;
    }

    return false;
  }

  return true;
};

export const getOrderIntentPayload = ({
  entitlement,
  formState,
  linkedEntitlements,
  isBillingCycleChanged,
  makeImmediate = false,
  coBillTimestamp,
}: {
  entitlement: EntitlementDetails;
  formState: FormState;
  linkedEntitlements: LinkedDestinationEntitlement[];
  isBillingCycleChanged: boolean;
  makeImmediate?: boolean;
  coBillTimestamp?: number | null;
}): OrderIntentRequest => {
  let linkedEntitlementsData: LinkedEntitlementsInstance[] = [];
  if (
    (isBillingCycleChanged || formState.billingFrequency === BillingFrequency.ANNUAL) &&
    linkedEntitlements?.length > 0
  ) {
    const currentEntitlementDetail: LinkedEntitlementsInstance = {
      entitlementId: entitlement.id,
      productId: entitlement.productId ?? '',
      offeringId: formState.offering?.value,
    };

    const linkedEntitlementsWithFormValues = linkedEntitlements.map(
      (linkedEntitlement: LinkedDestinationEntitlement): LinkedEntitlementsInstance => {
        const linkedEntitlementFormState = formState[linkedEntitlement.entitlementId as keyof FormState] as FormState;
        const { promotionIds, customPromotions } = getPromotionIds(
          linkedEntitlementFormState,
          isBillingCycleChanged,
          linkedEntitlement
        );
        return {
          promotionIds,
          customPromotions,
          entitlementId: linkedEntitlement.entitlementId,
          productId: linkedEntitlement.productId as string,
          chargeElement:
            linkedEntitlementFormState.pricingPlan?.chargeElement ||
            getChargeElement(linkedEntitlement?.productId as string),
          pricingPlanId: linkedEntitlementFormState?.pricingPlan?.value,
          pricingPlanType: linkedEntitlementFormState?.pricingPlan?.type as PricingPlanType,
          planType:
            linkedEntitlementFormState?.pricingPlan?.pricingPlanType ??
            defaultTo(get(linkedEntitlement, 'pricingPlan.pricingPlanType'), null),
          skipTrial: linkedEntitlementFormState.skipTrialEligible ? linkedEntitlementFormState?.skipTrial : undefined,
          offeringId: linkedEntitlementFormState?.offering?.value,
          userTier: linkedEntitlementFormState?.userTier?.value?.toString(),
          isImmediate:
            isImmediateAmendmentToFreeOrder(
              linkedEntitlement,
              linkedEntitlementFormState?.offering as OfferingOption
            ) || makeImmediate,
        };
      }
    );

    linkedEntitlementsData = [currentEntitlementDetail, ...linkedEntitlementsWithFormValues];
  }
  const { promotionIds, customPromotions } = getPromotionIds(formState, isBillingCycleChanged, entitlement);
  const orderIntentPayload: OrderIntentRequest = {
    transactionAccountId: defaultTo(entitlement?.transactionAccountId, ''),
    invoiceGroupId: defaultTo(formState?.invoiceGroupId?.value, ''),
    entitlementId: entitlement.id,
    orderType: OrderItemType.AMENDMENT_ORDER,
    offeringId: formState?.offering?.value ?? entitlement?.offeringId ?? undefined,
    units: (formState?.units ?? formState?.userTier?.value)?.toString(),
    billingFrequency: (formState?.billingFrequency as unknown) as PricingPlanCycle,
    pricingPlanType: formState?.pricingPlan?.type as PricingPlanType,
    planType:
      formState?.pricingPlan?.pricingPlanType ?? defaultTo(get(entitlement, 'pricingPlan.pricingPlanType'), null),
    promotionIds,
    customPromotions,
    pricingPlanId: formState?.pricingPlan?.value,
    skipTrial: formState.skipTrialEligible ? formState?.skipTrial : undefined,
    linkedEntitlements: linkedEntitlementsData,
    isBillingCycleChanged: isBillingCycleChanged && !isStatuspage(entitlement.productId),
    isImmediate: isImmediateAmendmentToFreeOrder(entitlement, formState?.offering as OfferingOption) || makeImmediate,
  };

  let endTimestampValue = formState?.endTimestamp;
  let billingAnchorTimestampValue = formState?.nextBillingAnchorTimestamp;
  const coBillTimestampValue = coBillTimestamp ?? formState.coBillTimestamp;

  if (coBillTimestampValue) {
    if (endTimestampValue) {
      endTimestampValue = coBillTimestampValue;
    } else {
      billingAnchorTimestampValue = coBillTimestampValue;
    }
  }

  if (endTimestampValue) {
    orderIntentPayload.endsAt = {
      type: EndsAtType.TIMESTAMP,
      value: endTimestampValue,
    };
  }

  if (billingAnchorTimestampValue) {
    orderIntentPayload.nextBillingAnchorTimestamp = `${billingAnchorTimestampValue}`;
  }

  return orderIntentPayload;
};

export const getCommonEntitlementDetails = (formStateValues: FormState) => {
  let endTimestampValue = formStateValues?.endTimestamp;
  let billingAnchorTimestampValue = formStateValues?.nextBillingAnchorTimestamp;

  if (formStateValues.coBillTimestamp) {
    if (endTimestampValue) {
      endTimestampValue = formStateValues.coBillTimestamp;
    } else {
      billingAnchorTimestampValue = formStateValues.coBillTimestamp;
    }
  }

  return {
    isImmediate: formStateValues?.immediateAmendment ? formStateValues.immediateAmendment : undefined,
    nextBillingAnchorTimestamp: billingAnchorTimestampValue ? billingAnchorTimestampValue : undefined,
    endsAt: endTimestampValue
      ? {
          type: EndsAtType.TIMESTAMP,
          value: endTimestampValue,
        }
      : undefined,
  };
};

export const getChargingDetails = (
  formStateValues: FormState,
  entitlement: EntitlementDetails | LinkedDestinationEntitlement,
  hasOptedForUpgradeTrial?: boolean
) => {
  let pricingPlanId, chargeElement, chargeType;
  // Indicates user is moving to PAID premium in this case premium pricingPlan ID should be send .
  if (!isNil(hasOptedForUpgradeTrial) && entitlement?.upgradeTrial) {
    pricingPlanId = entitlement.upgradeTrial.pricingPlan?.id;
    chargeType = entitlement.upgradeTrial.pricingPlan?.chargeType as string;
    chargeElement = getChargeElement(entitlement?.productId as string);
  } else {
    pricingPlanId = formStateValues?.pricingPlan?.value;
    chargeElement = formStateValues?.pricingPlan?.chargeElement ?? getChargeElement(entitlement?.productId as string);
    chargeType = formStateValues?.pricingPlan?.chargeType as string;
  }
  return { pricingPlanId, chargeElement, chargeType };
};

export const getChargeQuantity = ({
  formStateValues,
  entitlement,
  billingFrequency,
}: {
  formStateValues: FormState;
  entitlement: EntitlementDetails | LinkedDestinationEntitlement;
  billingFrequency: BillingFrequency;
}) => {
  const isStatusPageProduct = isStatuspage(entitlement?.productId);
  let chargeQuantity;
  if (isStatusPageProduct) {
    chargeQuantity = defaultTo(toNumber(formStateValues?.units), undefined);
  } else if (billingFrequency === BillingFrequency.ANNUAL) {
    chargeQuantity = formStateValues.userTier?.value;
  } else {
    chargeQuantity = defaultTo(toNumber(entitlement?.unitCount), undefined);
  }
  return chargeQuantity;
};

export const isInvalidCutShortTrial = ({
  updatedFormState,
  pristineFormState,
  entitlementDetails,
}: {
  updatedFormState: FormState;
  pristineFormState: FormState;
  entitlementDetails: EntitlementDetails | LinkedDestinationEntitlement;
}) => {
  const isStatusPageProduct = isStatuspage(entitlementDetails?.productId);
  const isExistingAnnualPlanForJCA =
    entitlementDetails?.billingFrequency === BillingFrequency.ANNUAL && !isStatusPageProduct;
  const isEntitlementOnTrial =
    isTrialEntitlement(entitlementDetails, isStatusPageProduct) || !!entitlementDetails.upgradeTrial;
  const isCutShortTrialWithOtherModification =
    updatedFormState.skipTrial &&
    (updatedFormState?.offering?.value !== pristineFormState?.offering?.value ||
      updatedFormState?.userTier?.value !== pristineFormState?.userTier?.value ||
      updatedFormState?.pricingPlan?.value !== pristineFormState?.pricingPlan?.value);

  return !!(isExistingAnnualPlanForJCA && isEntitlementOnTrial && isCutShortTrialWithOtherModification);
};
